import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { func, string, bool } from 'prop-types';
import Modal from 'react-modal';
import cn from 'classnames';

import { noScroll } from '~utils/scroll';
import ModalActions from '~redux/Modal/actions';

import styles from './styles.module.scss';

function CustomModal({
  children,
  hideCloseButton,
  className,
  overlayClassName,
  shouldReturnFocusAfterClose,
  shouldCloseOnOverlayClick,
  shouldCloseOnEsc,
  disableCloseButton,
  onClose,
  modal,
  isOpen,
  defaultClassName,
  ...props
}) {
  const dispatch = useDispatch();
  const closeModal = () => dispatch(ModalActions.closeModal(modal));
  const clearAllModals = () => dispatch(ModalActions.clearModals());

  const clearModals = () => {
    if (onClose) {
      onClose();
    }
    clearAllModals();
  };

  useEffect(() => {
    window.addEventListener('popstate', clearModals, false);
    return () => {
      noScroll.off();
      window.removeEventListener('popstate', clearModals);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevIsOpen = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      noScroll.on();
    }
    if (!isOpen && prevIsOpen.current) {
      noScroll.off();
    }
    prevIsOpen.current = isOpen;
  }, [prevIsOpen, isOpen]);

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
    closeModal(modal);
  };

  return (
    <Modal
      {...props}
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      className={cn({ [styles.modalContainer]: defaultClassName }, className)}
      overlayClassName={cn(styles.modalOverlay, overlayClassName)}
      shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
      bodyOpenClassName={styles.body}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}>
      <>
        {!hideCloseButton && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleCloseModal}
            disabled={disableCloseButton}>
            <i className={`icon-close ${styles.iconClose}`} />
          </button>
        )}
        {children}
      </>
    </Modal>
  );
}

CustomModal.propTypes = {
  modal: string.isRequired,
  className: string,
  defaultClassName: bool,
  disableCloseButton: bool,
  hideCloseButton: bool,
  isOpen: bool,
  overlayClassName: string,
  shouldCloseOnEsc: bool,
  shouldCloseOnOverlayClick: bool,
  shouldReturnFocusAfterClose: bool,
  onClose: func
};

CustomModal.defaultProps = {
  defaultClassName: true,
  shouldCloseOnEsc: true,
  shouldCloseOnOverlayClick: true,
  shouldReturnFocusAfterClose: false
};

export default CustomModal;
